import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Dialog from '../../../components/ui/Dialog';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import { useAuth } from '../../../hooks/useAuth';
import { useNominationToast } from '../../../hooks/useNominationToast';
import { getNominationErrorMessage } from '../../../utils/nominationErrorUtils';
import { hodReviewService } from '../../../services/hodReviewService';
import tuneIcon from '../../../assets/tune.svg';
import arrowDropDownIcon from '../../../assets/arrow_drop_down_filter.svg';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import Pagination from '../../../components/ui/Pagination';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';





// Constants
const PageSizeOptions = [10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 10;
const INITIAL_PAGE = 1;
const TABLE_GRID_COLUMNS = 13;

// Grid Column Spans
const EMPLOYEE_ID_SPAN = 2;
const EMPLOYEE_NAME_SPAN = 3;
const DESIGNATION_SPAN = 2;
const DEPARTMENT_SPAN = 2;
const MANAGER_NAME_SPAN = 2;
const STATUS_SPAN = 1;
const RATING_SPAN = 1;

// Team Review interface
interface TeamReview {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  managerEmail: string;
  reviewStatus: string; // Using dynamic status from API instead of hardcoded values
  nominationStatus?: string;
  okrStatus?: string;
  rating?: string;
  employeeEmail: string;
  nominationRelationshipId: string;
  nominationId: string; // Add nomination_id for bulk API
}

const HODReviewPageView: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const hasActiveCycle = useActiveCycle();
  const { toastState, showSuccess, showError, closeToast } = useNominationToast();
  const [reviews, setReviews] = useState<TeamReview[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Track if data has been fetched to prevent duplicate API calls
  const hasFetched = React.useRef(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    department: '',
    designation: '',
    reviewStatus: ''
  });
  const [tempFilters, setTempFilters] = useState({
    department: '',
    designation: '',
    reviewStatus: ''
  });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showBulkReviewDialog, setShowBulkReviewDialog] = useState(false);

  // Fetch team reviews from API
  const fetchTeamReviews = useCallback(async () => {
    if (authLoading || !user?.email) {
      return;
    }
    
    // Prevent duplicate API calls
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    setLoading(true);
    
    try {
      console.log('🔄 Fetching team reviews for user:', user.email);
      
      // Call the real API
      const response = await hodReviewService.getHodNominationReviewList(user.email);
      
      if (response.status && response.data) {
        // Handle the actual API response structure with nominators array
        let reviewList = [];
        
        if (response.data.nominators && Array.isArray(response.data.nominators)) {
          reviewList = response.data.nominators;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          reviewList = response.data.data;
        } else if (Array.isArray(response.data)) {
          reviewList = response.data;
        }
        
        // Transform API data to our interface
        const transformedReviews: TeamReview[] = reviewList.map((item: unknown) => {
          const reviewItem = item as Record<string, unknown>;
          const nominatorManager = reviewItem.nominator_manager as Record<string, unknown> | undefined;
          return {
            id: reviewItem.nominator_id?.toString() || String(Math.random()),
            employeeId: (reviewItem.employee_id as string) || '',
            employeeName: (reviewItem.nominator_name as string) || '',
            designation: (reviewItem.nominator_designation as string) || '',
            department: (reviewItem.nominator_department as string) || '',
            managerName: (nominatorManager?.manager_name as string) || '',
            managerEmail: (nominatorManager?.manager_email as string) || '',
            reviewStatus: (reviewItem.review_exists as boolean) ? 'submitted' : 'pending',
            nominationStatus: (reviewItem.nomination_status as string) || '',
            okrStatus: (reviewItem.okr_status as string) || '',
            rating: (reviewItem.rating as string) || '',
            employeeEmail: (reviewItem.nominator_email as string) || '',
            nominationRelationshipId: reviewItem.nomination_relationship_id?.toString() || '',
            nominationId: reviewItem.nomination_id?.toString() || ''
          };
        });
        
        console.log('✅ Team reviews loaded from API:', transformedReviews);
        setReviews(transformedReviews);
      } else {
        console.error('❌ Failed to fetch team reviews:', response.error);
        setReviews([]);
      }
    } catch (error) {
      console.error('❌ Error fetching team reviews:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email, authLoading]);

  useEffect(() => {
    fetchTeamReviews();
  }, [fetchTeamReviews]);

  // Listen for global clearFilters event from SearchFilterBar
  useEffect(() => {
    const handler = () => {
      handleRemoveAllFilters();
    };

    window.addEventListener('clearFilters', handler as EventListener);
    return () => window.removeEventListener('clearFilters', handler as EventListener);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Filter reviews based on search term and filters with useMemo optimization
  const filteredReviews = useMemo(() => {
    return reviews.filter(review => {
      const matchesSearch = !searchTerm || (
        review.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.managerName.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesDepartment = !filters.department || review.department === filters.department;
      const matchesDesignation = !filters.designation || review.designation === filters.designation;
      const matchesStatus = !filters.reviewStatus || review.reviewStatus === filters.reviewStatus;

      return matchesSearch && matchesDepartment && matchesDesignation && matchesStatus;
    });
  }, [reviews, searchTerm, filters.department, filters.designation, filters.reviewStatus]);

  // Debug logging
  console.log('🔍 Debug - reviews array:', reviews);
  console.log('🔍 Debug - reviews length:', reviews.length);
  console.log('🔍 Debug - filteredReviews length:', filteredReviews.length);
  console.log('🔍 Debug - searchTerm:', searchTerm);
  console.log('🔍 Debug - filters:', filters);

  const totalItems = filteredReviews.length;
  const pageCount = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * (pageSize === 0 ? totalItems : pageSize);
  const endIndex = pageSize === 0 ? totalItems : Math.min(startIndex + pageSize, totalItems);
  const currentReviews = pageSize === 0 ? filteredReviews : filteredReviews.slice(startIndex, endIndex);

  // Handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (value: number) => {
    setPageSize(value);
    setPage(INITIAL_PAGE);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(INITIAL_PAGE);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleRemoveAllFilters = () => {
    const emptyFilters = {
      department: '',
      designation: '',
      reviewStatus: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSearchTerm('');
    setPage(INITIAL_PAGE);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowFilters(false);
    setPage(INITIAL_PAGE);
  };

  const handleOpenFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
    setTempFilters(filters);
  };

  // Selection handlers - only allow selecting pending reviews
  const handleSelectReview = (id: string) => {
    // Find the review and check if it's pending (based on nominationStatus)
    const review = reviews.find(r => r.id === id);
    if (!review || review.nominationStatus?.toLowerCase() !== 'pending') {
      return; // Don't allow selection of non-pending reviews
    }
    
    setSelectedReviews(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  // Get only pending reviews for selection (from current page for UI)
  const pendingReviews = currentReviews.filter(r => r.nominationStatus?.toLowerCase() === 'pending');
  
  // Get all pending reviews from full list for accurate count
  const allPendingReviews = reviews.filter(r => r.nominationStatus?.toLowerCase() === 'pending');
  
  // Filter selected reviews to only include those that are actually pending
  const selectedPendingReviews = selectedReviews.filter(id => 
    allPendingReviews.some(r => r.id === id)
  );

  const handleSelectAll = () => {
    if (selectedPendingReviews.length === pendingReviews.length && pendingReviews.length > 0) {
      // Unselect all pending reviews
      setSelectedReviews(prev => prev.filter(id => 
        !pendingReviews.some(r => r.id === id)
      ));
    } else {
      // Select all pending reviews (keep existing non-pending selections if any)
      setSelectedReviews(prev => [
        ...prev.filter(id => !pendingReviews.some(r => r.id === id)), // Remove any existing pending selections
        ...pendingReviews.map(r => r.id) // Add all pending reviews
      ]);
    }
  };

  // Bulk Review Handler
  const handleBulkReview = () => {
    if (!user?.email || selectedPendingReviews.length === 0) {
      showError('No Selection', 'No pending reviews selected');
      return;
    }
    
    setShowBulkReviewDialog(true);
  };

    /**
   * Confirm and execute bulk review
   */
  const handleConfirmBulkReview = async () => {
    if (!user?.email || selectedPendingReviews.length === 0) {
      showError('No Selection', 'No pending reviews selected');
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare review data for bulk action API - only include pending reviews
      const selectedReviewsData = selectedPendingReviews.map(reviewId => {
        const review = reviews.find(r => r.id === reviewId);
        // Double-check that the review is pending before including (based on nominationStatus)
        if (!review || review.nominationStatus?.toLowerCase() !== 'pending') {
          return null;
        }
        return {
          nomination_id: parseInt(review.nominationId || '0'), // Use the nomination_id from API
          nominator_email: review.employeeEmail || ''
        };
      }).filter((r): r is { nomination_id: number; nominator_email: string } => 
        r !== null && r.nominator_email !== '' && r.nomination_id > 0
      ); // Only include valid pending reviews with valid email and nomination_id
      
      if (selectedReviewsData.length === 0) {
        showError('Data Error', 'Unable to find valid data for selected reviews');
        return;
      }
      
      console.log('🔄 Bulk reviewing nominations:', selectedReviewsData);
      
      // Use the new bulk review API
      const response = await hodReviewService.performBulkHodReview(
        user.email,
        selectedReviewsData
      );
      
      if (response.status && response.data) {
        const successCount = selectedReviewsData.length;
        
        showSuccess(
          'Review Submitted', 
          `${successCount} review${successCount > 1 ? 's' : ''} submitted successfully`
        );
        
        // Refresh the list and clear selections
        hasFetched.current = false;
        await fetchTeamReviews();
        setSelectedReviews([]);
        setShowBulkReviewDialog(false);
      } else {
        showError('Review Failed', response.message || (typeof response.error === 'string' ? response.error : null) || 'Failed to submit reviews');
        setShowBulkReviewDialog(false);
      }
    } catch (error) {
      console.error('❌ Error in bulk review:', error);
      setShowBulkReviewDialog(false);
      showError('Review Failed', getNominationErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBulkReview = () => {
    setShowBulkReviewDialog(false);
  };

  // Navigation handler
  const handleViewDetails = (review: TeamReview) => {
    navigate(`/hod-review/${review.nominationRelationshipId}`, {
      state: {
        employeeId: review.employeeId,
        employeeEmail: review.employeeEmail,
        employeeName: review.employeeName,
        designation: review.designation,
        department: review.department,
        managerName: review.managerName,
        managerEmail: review.managerEmail
      }
    });
  };

  // Get unique values for filters - dynamically from API data
  const uniqueDepartments = [...new Set(reviews.map(r => r.department).filter(Boolean))].sort();
  const uniqueDesignations = [...new Set(reviews.map(r => r.designation).filter(Boolean))].sort();
  
  // Status options dynamically from API data
  const uniqueStatuses = [...new Set(reviews.map(r => r.nominationStatus).filter(Boolean))].sort() as string[];

  const hasActiveFilters = filters.department || filters.designation || filters.reviewStatus;

  // Helper function to convert status to title case
  const toTitleCase = (str: string) => {
    if (str.toUpperCase() === 'N/A') return 'N/A';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to format rating display (matching HOD Detail page style)
  const formatRatingDisplay = (rating: string | undefined) => {
    if (!rating) return 'N/A';
    const numRating = Number(rating);
    switch (numRating) {
      case 1:
        return '1-Excel';
      case 2:
        return '2-Fully Met';
      case 3:
        return '3-Mostly Met';
      case 4:
        return '4-Requires Action';
      default:
        return rating || 'N/A';
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading team reviews...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-600">No user data available. Please log in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Section */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-grow">
          <SearchFilterBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onClearSearch={() => handleSearchChange('')}
            onOpenFilters={handleOpenFilters}
            hasActiveFilters={!!hasActiveFilters}
            activeFiltersCount={[filters.department, filters.designation, filters.reviewStatus].filter(Boolean).length}
            tuneIcon={tuneIcon}
          />
        </div>
        
        <div className="flex-shrink-0 lg:min-w-[12rem]">
          {/* Bulk Action Button - Only show when pending reviews are selected */}
          {selectedPendingReviews.length > 0 && (
            <div className="flex justify-start gap-4 lg:justify-end">
              <button
                onClick={handleBulkReview}
                className="px-6 py-2 bg-primary-800 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Review ({selectedPendingReviews.length})
              </button>
            </div>
          )}
        </div>
      </div>

      <FiltersModal
        show={showFilters}
        tempFilters={{
          department: tempFilters.department,
          designation: tempFilters.designation,
          nominationCapacity: tempFilters.reviewStatus
        }}
        uniqueDepartments={uniqueDepartments}
        uniqueDesignations={uniqueDesignations}
        uniqueNominationCapacities={uniqueStatuses}
        onChange={(filterType: string, value: string) => {
          // Map nominationCapacity back to reviewStatus
          const mappedFilterType = filterType === 'nominationCapacity' ? 'reviewStatus' : filterType;
          handleFilterChange(mappedFilterType, value);
        }}
        onApply={handleApplyFilters}
        onRemoveAll={handleRemoveAllFilters}
        onClose={handleCloseFilters}
        arrowDropDownIcon={arrowDropDownIcon}
        isHODReviewPage={true}
      />

      {/* Table Card */}
      <Card>
        <div className="table-container">
          {/* Table Header */}
          <div className="bg-primary-800 rounded-lg px-6 py-3 border-b border-gray-200 mb-2">
            <div className={`grid grid-cols-${TABLE_GRID_COLUMNS} gap-4 text-left text-base font-medium text-white tracking-wider`}>
              <div className={`text-base col-span-${EMPLOYEE_ID_SPAN}`}>
                <input
                  type="checkbox"
                  checked={selectedPendingReviews.length === pendingReviews.length && pendingReviews.length > 0}
                  onChange={handleSelectAll}
                  disabled={pendingReviews.length === 0}
                  className={`rounded border-gray-300 w-4 h-4 mr-4 ${
                    pendingReviews.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
                Employee ID
              </div>
              <div className={`text-base col-span-${EMPLOYEE_NAME_SPAN}`}>Employee Name</div>
              <div className={`text-base col-span-${DESIGNATION_SPAN}`}>Designation</div>
              <div className={`text-base col-span-${DEPARTMENT_SPAN}`}>Department</div>
              <div className={`text-base col-span-${MANAGER_NAME_SPAN}`}>Manager Name</div>
              <div className={`text-base col-span-${STATUS_SPAN}`}>Status</div>
              <div className={`text-base col-span-${RATING_SPAN}`}>Rating</div>
              {/* <div className={`text-base col-span-${ACTION_SPAN}`}>Action</div> */}
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-3 relative">
            {currentReviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Team Reviews Found</h3>
                <p className="text-gray-500 mb-4">
                  {hasActiveFilters 
                    ? 'No team reviews match your current search criteria.'
                    : 'No team reviews available.'
                  }
                </p>
              </div>
            ) : (
              currentReviews.map((review) => (
                <div 
                  key={review.id} 
                  className="hover:bg-gray-50 border border-gray-200 rounded-lg px-6 py-2 transition-colors relative cursor-pointer"
                  onClick={() => handleViewDetails(review)}
                >
                  <div className={`grid grid-cols-${TABLE_GRID_COLUMNS} gap-4 items-center`}>
                    <div className={`col-span-${EMPLOYEE_ID_SPAN} flex items-center`}>
                      <input 
                        type="checkbox"
                        checked={selectedReviews.includes(review.id)}
                        onChange={() => handleSelectReview(review.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={review.nominationStatus?.toLowerCase() !== 'pending'}
                        className={`mr-4 rounded border-gray-300 w-4 h-4 ${
                          review.nominationStatus?.toLowerCase() !== 'pending' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                      <div className="text-base text-gray-600">{review.employeeId}</div>
                    </div>
                    <div className={`col-span-${EMPLOYEE_NAME_SPAN}`}>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600"
                        name={review.employeeName}
                        email={review.employeeEmail}
                      />
                    </div>
                    <div className={`col-span-${DESIGNATION_SPAN}`}>
                      <div className="text-base text-gray-600">{review.designation}</div>
                    </div>
                    <div className={`col-span-${DEPARTMENT_SPAN}`}>
                      <div className="text-base text-gray-600">{review.department}</div>
                    </div>
                    <div className={`col-span-${MANAGER_NAME_SPAN}`}>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600"
                        name={review.managerName}
                        email={review.managerEmail}
                      />
                    </div>
                    <div className={`col-span-${STATUS_SPAN}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(review.nominationStatus??"N/A")}`}>
                        {toTitleCase(review.nominationStatus ==="approved"?"submitted":review.nominationStatus??"N/A")}
                      </span>
                    </div>
                    <div className={`col-span-${RATING_SPAN}`}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {formatRatingDisplay(review.rating)}
                      </span>
                    </div>
                    {/* <div className={`col-span-${ACTION_SPAN}`}>
                      <div className="flex items-center justify-center">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu(review.id, e);
                            }}
                            className="p-2 text-black hover:text-gray-600 focus:outline-none transition-colors rounded-full hover:bg-gray-100"
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          
                        
                          {openMenuId === review.id && (
                            <div 
                              className={`absolute right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[${DROPDOWN_Z_INDEX}] ${
                                menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                <button 
                                  onClick={() => handleViewDetails(review)}
                                  className="flex items-center w-full px-4 py-2 text-base text-black hover:bg-gray-100"
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination - Only show when there are results */}
        {pageSize > 0 && totalItems > 0 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handleItemsPerPageChange}
              pageSizeOptions={PageSizeOptions}
            />
          </div>
        )}
      </Card>

      {/* Toast Notifications */}
      <Dialog
        isOpen={toastState.isOpen}
        onClose={closeToast}
        title={toastState.title}
        message={toastState.message}
        type={toastState.type}
        confirmText={toastState.confirmText}
        onConfirm={toastState.onConfirm || closeToast}
        showCancel={toastState.showCancel}
        cancelText={toastState.cancelText}
      />

      {/* Bulk Review Confirmation Dialog */}
      <Dialog
        isOpen={showBulkReviewDialog}
        onClose={handleCancelBulkReview}
        title="Confirm Bulk Review Submission"
        message={`Are you sure you want to submit ${selectedPendingReviews.length} review${selectedPendingReviews.length > 1 ? 's' : ''} at once? All selected reviews will be submitted in a single batch.`}
        type="warning"
        confirmText="Submit Reviews"
        cancelText="Cancel"
        onConfirm={handleConfirmBulkReview}
        showCancel={true}
      />
    </div>
  );
};
export default HODReviewPageView;
